import { inject } from '@angular/core';
import { CanActivateChildFn, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { map, of } from 'rxjs';

import { UnsavedDialogsService } from '@core/unsaved-dialogs.service';
import { confirmDiscardChanges } from '@shared/components/confirmation-dialog/discard-changes';

/**
 * Asks before browser back/forward navigation discards a dirty form dialog.
 *
 * Dialogs are configured with `closeOnNavigation: false` (see main.ts), because Material's default
 * disposes them on popstate before any guard can run. `closeDialogsOnHistoryNavigation` closes the
 * unprotected ones as the navigation starts; this guard closes the protected form dialogs once the
 * navigation is allowed to proceed.
 */
export const unsavedDialogsGuard: CanActivateChildFn = () => {
  if (inject(Router).currentNavigation()?.trigger !== 'popstate') {
    return of(true);
  }

  const dialog = inject(MatDialog);
  const unsavedDialogs = inject(UnsavedDialogsService);

  if (!unsavedDialogs.hasDirtyDialogs()) {
    unsavedDialogs.discardAll();
    return of(true);
  }

  return confirmDiscardChanges(dialog).pipe(
    map((confirmed) => {
      if (confirmed) {
        unsavedDialogs.discardAll();
      }
      return confirmed;
    })
  );
};
